<template>
  <div class="container mx-auto px-4 py-8">
    <!-- Header / Next Race Section -->
    <section class="mb-8">
      <NextRaceCard :race="nextRace" />
    </section>

    <!-- Weather & Last Race Grid -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
      <WeatherCard class="h-full" :weather="weather" :location-name="locationName" />
      <LastRaceResult class="h-full" :results="lastRaceResults" :sprint-results="lastSprintResults" />
    </div>

    <!-- News Section -->
    <section class="mb-8" v-if="isLoading || featuredNews || latestNews.length > 0">
      <h2 class="text-2xl font-bold mb-4 text-white border-l-4 border-red-600 pl-3">
        Ultime Notizie F1 <span class="text-xs font-normal text-gray-500 ml-2">powered by <a
            href="https://f1grandprix.motorionline.com/" target="_blank"
            class="hover:underline hover:text-red-600 transition-colors">motorionline.com</a></span>
      </h2>

      <div v-if="isLoading" class="w-full h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-8">
        <div class="h-full bg-red-600 animate-indeterminate w-full"></div>
      </div>

      <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Featured News (Left Column - Spans 2 cols on Desktop) -->
        <div v-if="featuredNews" @click="openArticle(featuredNews.link)"
          class="lg:col-span-2 group cursor-pointer flex flex-col rounded-xl shadow-lg bg-white dark:bg-gray-800 transition-transform hover:scale-[1.01] overflow-hidden h-auto lg:h-[500px]">
          <div class="aspect-video lg:aspect-auto lg:flex-1 w-full overflow-hidden">
            <img :src="featuredNews.image" :alt="featuredNews.title"
              class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110">
          </div>
          <div class="p-6 flex flex-col justify-between shrink-0">
            <p
              class="text-xl md:text-2xl font-bold text-gray-800 dark:text-white mb-4 leading-tight group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
              {{ featuredNews.subtitle }}
            </p>
            <div
              class="flex items-center text-red-600 dark:text-red-400 font-bold text-sm uppercase tracking-wide group-hover:underline">
              Leggi articolo completo
              <svg xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 ml-1 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Latest News List (Right Column - Spans 1 col) -->
        <div
          class="lg:col-span-1 bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden flex flex-col h-[500px]">
          <div
            class="p-4 bg-gray-100 dark:bg-gray-700 font-bold text-lg border-b dark:border-gray-600 flex justify-between items-center shrink-0">
            <span>Altre Notizie</span>
            <span
              class="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded-full text-gray-600 dark:text-gray-300">{{
                latestNews.length }}</span>
          </div>
          <div class="overflow-y-auto flex-1 p-2 space-y-2 custom-scrollbar">
            <div v-for="(news, index) in latestNews" :key="index" @click="openArticle(news.link)"
              class="flex gap-3 p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg cursor-pointer transition-colors group">
              <div class="w-24 h-16 flex-shrink-0 rounded overflow-hidden">
                <img :src="news.image" :alt="news.title" loading="lazy"
                  class="w-full h-full object-cover transition-transform group-hover:scale-105">
              </div>
              <div class="flex-1 min-w-0 flex flex-col justify-center">
                <span class="text-xs text-red-600 dark:text-red-400 font-semibold mb-0.5 truncate">{{ news.category
                  }}</span>
                <h4
                  class="text-sm font-bold text-gray-800 dark:text-gray-100 leading-tight line-clamp-2 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                  {{ news.title }}
                </h4>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Article Modal -->
    <Teleport to="body">
      <div v-if="isModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity" @click="closeModal"></div>

        <!-- Modal Content -->
        <div
          class="relative w-full max-w-4xl max-h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">

          <!-- Header Actions -->
          <div
            class="flex justify-between items-center p-4 border-b dark:border-gray-800 bg-white dark:bg-gray-900 z-10">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-widest">Dettaglio Notizia</span>
            <button @click="closeModal"
              class="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-500 hover:text-red-600">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Loading State -->
          <div v-if="isLoadingArticle" class="flex-1 flex flex-col items-center justify-center p-20">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            <p class="mt-4 text-gray-500 animate-pulse">Caricamento articolo...</p>
          </div>

          <!-- Article Content -->
          <div v-else-if="currentArticle" class="overflow-y-auto p-6 md:p-10 custom-scrollbar">
            <header class="mb-8 text-center">
              <span
                class="inline-block px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-bold rounded-full mb-4">News</span>
              <h1 class="text-2xl md:text-4xl font-black text-gray-900 dark:text-white mb-4 leading-tight">{{
                currentArticle.title }}</h1>
              <p class="text-lg md:text-xl text-gray-600 dark:text-gray-300 font-medium italic">{{
                currentArticle.subtitle }}</p>
            </header>

            <figure class="mb-8 rounded-xl overflow-hidden shadow-md">
              <img :src="currentArticle.main_image" :alt="currentArticle.title" class="w-full h-auto object-cover">
            </figure>

            <div
              class="prose prose-lg dark:prose-invert max-w-none prose-img:rounded-xl prose-headings:text-red-600 dark:prose-headings:text-red-500">
              <template v-for="(item, index) in currentArticle.content" :key="index">

                <!-- Text Paragraph -->
                <p v-if="item.type === 'text'" v-html="item.content"
                  class="mb-4 text-gray-700 dark:text-gray-300 leading-relaxed"></p>

                <!-- Headers -->
                <h3 v-else-if="item.type === 'header'"
                  class="text-xl font-bold mt-8 mb-4 text-gray-800 dark:text-white border-l-4 border-red-600 pl-3">
                  {{ item.content }}
                </h3>

                <!-- Images in content -->
                <figure v-else-if="item.type === 'image'" class="my-6">
                  <img :src="item.src" alt="Article image" class="w-full rounded-lg shadow-sm">
                </figure>

              </template>
            </div>

            <div class="mt-10 pt-6 border-t dark:border-gray-800 text-center text-sm text-gray-500">
              Fonte: <a :href="currentArticle.link" target="_blank"
                class="text-red-600 hover:underline font-bold">Motorionline</a>
            </div>
          </div>

          <!-- Error State -->
          <div v-else class="p-10 text-center">
            <p class="text-red-500">Impossibile caricare l'articolo.</p>
          </div>

        </div>
      </div>
    </Teleport>

    <!-- Standings Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
      <StandingsCard title="Top 5 Piloti" :list="driverStandings" type="driver" />
      <StandingsCard title="Top 5 Costruttori" :list="constructorStandings" type="constructor" />
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { f1Service } from '../services/f1Service';
import { weatherService } from '../services/weatherService';
import { newsService } from '../services/newsService';
import NextRaceCard from '../components/NextRaceCard.vue';
import WeatherCard from '../components/WeatherCard.vue';
import LastRaceResult from '../components/LastRaceResult.vue';
import StandingsCard from '../components/StandingsCard.vue';

export default {
  name: 'HomeView',
  components: {
    NextRaceCard,
    WeatherCard,
    LastRaceResult,
    StandingsCard
  },
  setup() {
    const nextRace = ref(null);
    const lastRaceResults = ref(null);
    const lastSprintResults = ref(null);
    const driverStandings = ref([]);
    const constructorStandings = ref([]);
    const weather = ref(null);
    const locationName = ref('');

    // News State
    const featuredNews = ref(null);
    const latestNews = ref([]);
    const isModalOpen = ref(false);
    const currentArticle = ref(null);
    const isLoadingArticle = ref(false);
    const isLoading = ref(true);

    onMounted(async () => {
      // Fetch Next Race
      nextRace.value = await f1Service.getNextRace();

      // Fetch Weather if race location exists
      if (nextRace.value && nextRace.value.Circuit && nextRace.value.Circuit.Location) {
        const { lat, long, locality } = nextRace.value.Circuit.Location;
        locationName.value = locality;
        weather.value = await weatherService.getWeather(lat, long);
      }

      // Fetch other data
      lastRaceResults.value = await f1Service.getLastRaceResults();
      
      // Fetch the latest sprint results from any race this season
      const allRaces = await f1Service.getAllRaces();
      const now = new Date();
      const pastSprintRaces = allRaces
        .filter(r => r.Sprint && new Date(`${r.Sprint.date}T${r.Sprint.time || '00:00:00Z'}`) < now)
        .sort((a, b) => new Date(b.Sprint.date) - new Date(a.Sprint.date));
      
      if (pastSprintRaces.length > 0) {
        const latestSprintRace = pastSprintRaces[0];
        const sprintRes = await f1Service.getSprintResults(latestSprintRace.season, latestSprintRace.round);
        if (sprintRes && sprintRes.length > 0) {
          lastSprintResults.value = {
            raceName: latestSprintRace.raceName,
            season: latestSprintRace.season,
            sprintDate: latestSprintRace.Sprint.date,
            results: sprintRes
          };
        }
      }
      
      driverStandings.value = await f1Service.getDriverStandings('current', 5);
      constructorStandings.value = await f1Service.getConstructorStandings('current', 5);

      // Fetch News
      loadNews();
    });

    const loadNews = async () => {
      try {
        const [featured, latest] = await Promise.all([
          newsService.getFeaturedNews(),
          newsService.getLatestNews()
        ]);
        featuredNews.value = featured;
        latestNews.value = latest;
      } catch (error) {
        console.error("Error loading news:", error);
      } finally {
        isLoading.value = false;
      }
    };

    const openArticle = async (link) => {
      isModalOpen.value = true;
      isLoadingArticle.value = true;
      currentArticle.value = null;
      try {
        const article = await newsService.getArticle(link);
        currentArticle.value = { ...article, link };
      } catch (e) {
        console.error("Error loading article", e);
      } finally {
        isLoadingArticle.value = false;
      }
    };

    const closeModal = () => {
      isModalOpen.value = false;
      currentArticle.value = null;
    };

    return {
      nextRace,
      lastRaceResults,
      lastSprintResults,
      driverStandings,
      constructorStandings,
      weather,
      locationName,
      featuredNews,
      latestNews,
      isModalOpen,
      currentArticle,
      isLoadingArticle,
      openArticle,
      closeModal,
      isLoading
    };
  }
}

</script>

<style scoped>
@keyframes indeterminate {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

.animate-indeterminate {
  animation: indeterminate 1.5s infinite linear;
}
</style>
